import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { formatIsoDay, formatTRY } from "@/lib/format";
import { ITEM_TYPES, Stat, daysLabel } from "@/lib/finance";
import { EmptyRow, LoadingRow, PageHeader } from "@/components/common/States";

const TABS = [["gecikmis", "Gecikmiş"], ["0", "Bugün"], ["7", "7 Gün"], ["30", "30 Gün"], ["60", "60 Gün"], ["90", "90 Gün"]];
const TYPE_CLS = { fatura: "bg-slate-100 text-slate-700 border-slate-200", cek: "bg-blue-50 text-blue-700 border-blue-200", senet: "bg-indigo-50 text-indigo-700 border-indigo-200", soz: "bg-amber-50 text-amber-800 border-amber-200" };

const inTab = (tab, d) => (tab === "gecikmis" ? d < 0 : d >= 0 && d <= Number(tab));

export default function Upcoming() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("7");
  const [type, setType] = useState("");
  useEffect(() => { api.get("/tahsilatlar/upcoming").then(({ data }) => setData(data)); }, []);
  const items = useMemo(() => (data?.items || []).filter((i) => inTab(tab, i.days) && (!type || i.type === type)), [data, tab, type]);
  const h = data?.horizons?.[tab];
  return (
    <div data-testid="upcoming-page">
      <PageHeader title="Yaklaşan Tahsilatlar" subtitle="Vadesi gelen açık faturalar, portföydeki çek/senetler ve ödeme sözleri tek ekranda" testid="upcoming-title" />
      <Tabs value={tab} onValueChange={(v) => { setTab(v); setType(""); }} className="mb-3">
        <TabsList>{TABS.map(([k, l]) => (
          <TabsTrigger key={k} value={k} data-testid={`upcoming-tab-${k}`} className={k === "gecikmis" ? "data-[state=active]:text-rose-700" : ""}>
            {l}{data && <span className="ml-1.5 text-[10px] font-mono text-slate-400">{data.horizons[k].count}</span>}
          </TabsTrigger>))}
        </TabsList>
      </Tabs>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Stat label="Toplam" value={formatTRY(h?.amount)} hint={`${h?.count ?? "—"} kalem`} tone={tab === "gecikmis" ? "text-rose-600" : "text-blue-700"} active={!type} onClick={() => setType("")} testid="upcoming-total" />
        {Object.entries(ITEM_TYPES).map(([k, l]) => (
          <Stat key={k} label={l} value={formatTRY(h?.[k]?.amount || 0)} hint={`${h?.[k]?.count || 0} kalem`} active={type === k} onClick={() => setType(type === k ? "" : k)} testid={`upcoming-total-${k}`} />
        ))}
      </div>
      <div className="panel overflow-x-auto max-h-[calc(100vh-380px)]">
        <table className="erp-table" data-testid="upcoming-table">
          <thead><tr><th>Tür</th><th>Cari</th><th>Belge</th><th>Açıklama</th><th>Vade</th><th>Kalan</th><th className="text-right">Tutar</th><th>Durum</th></tr></thead>
          <tbody>
            {!data ? <LoadingRow cols={8} /> : items.length === 0 ? <EmptyRow cols={8} text="Bu aralıkta tahsilat beklenmiyor" /> : items.map((i) => (
              <tr key={`${i.type}-${i.id}`} className="cursor-pointer" onClick={() => navigate(i.link)} data-testid={`upcoming-row-${i.doc_no}`}>
                <td><span className={`text-[11px] font-medium px-1.5 py-0.5 rounded border ${TYPE_CLS[i.type]}`}>{i.type_label}</span></td>
                <td className="max-w-[220px] truncate"><div className="text-slate-800">{i.customer_title}</div><div className="text-[11px] font-mono text-slate-400">{i.customer_code} · {i.phone}</div></td>
                <td className="font-mono text-xs font-semibold">{i.doc_no}</td>
                <td className="max-w-[240px] truncate text-slate-500" title={i.description}>{i.description}</td>
                <td className="font-mono text-xs">{formatIsoDay(i.due_date)}</td>
                <td className={`text-xs ${i.days < 0 ? "text-rose-600 font-semibold" : i.days === 0 ? "text-amber-700 font-semibold" : "text-slate-500"}`}>{daysLabel(i.days)}</td>
                <td className="text-right font-mono font-semibold">{formatTRY(i.amount)}</td>
                <td className="text-xs">{i.status_label}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">Faturalar FIFO eşleşme sonrası kalan tutarla listelenir. Ödeme sözleri ilgili faturalarla aynı borcu temsil edebilir.</p>
    </div>
  );
}
